/* Utility widgets registry — Phase 3 embeddables that don't belong in the
 * SEO Tools section (countdown, crypto, weather, etc.). Each entry knows
 * how to render its own configuration UI in WidgetGallery and produce its
 * embed snippet.
 *
 * Option types supported by WidgetGallery's renderer:
 *   - "select"        single-select dropdown
 *   - "select-labeled"  select with {value,label} options
 *   - "text"          plain text input (optional `urlHint: true` for URL fields)
 *   - "number"        numeric input
 *   - "datetime"      datetime-local input (returns "YYYY-MM-DDThh:mm")
 *   - "boolean"       Yes/No dropdown (stores "1"/"0" under the hood)
 *   - "multi-search"  searchable, chip-based multi-select (uses picklist)
 *
 * Per-option conditional rendering & inline hints:
 *   - `showWhen: { key, equals }` — only render this option when another
 *     option holds the matching value. Used by countdown so the custom
 *     date field is hidden until the user picks the "custom" preset.
 *   - `emptyHint: "…"` — when the option value is empty/null, an amber
 *     inline hint is shown below the field. Used by countdown's
 *     `custom_date` so users aren't silently defaulted to a 24h
 *     fallback. Any new option can opt-in by adding this string.
 */
import {
  Cloud, Bitcoin, TrendingUp, Calendar, Globe, MessageSquare,
  ShieldCheck, Tv, Twitch as TwitchIcon, Sparkles, Gauge, Flame, Users, Activity,
  Star, Package, Hash, Flag as FlagIcon,
} from "lucide-react";
import { CITY_OPTIONS, CRYPTO_OPTIONS, STOCK_OPTIONS } from "./widget_picklists";

const SIZE_OPTIONS = [
  { value: "sm", label: "Small" },
  { value: "md", label: "Medium" },
  { value: "lg", label: "Large" },
];
const SIZE_OPTIONS_HCP = [
  { value: "hero", label: "Hero" },
  { value: "card", label: "Card" },
  { value: "pill", label: "Pill" },
];

// Width/height presets reused across widgets that benefit from a container
// override. Values flow through as `w=`/`h=` query params; widget scripts
// apply them to their root wrap element so the user can size the embed to
// fit any layout slot.
const WIDTH_OPTIONS = [
  { value: "", label: "Auto" },
  { value: "240", label: "240 px" },
  { value: "320", label: "320 px" },
  { value: "420", label: "420 px" },
  { value: "560", label: "560 px" },
  { value: "720", label: "720 px" },
  { value: "100%", label: "Full width" },
];
const HEIGHT_OPTIONS = [
  { value: "", label: "Auto" },
  { value: "120", label: "120 px" },
  { value: "180", label: "180 px" },
  { value: "240", label: "240 px" },
  { value: "320", label: "320 px" },
  { value: "420", label: "420 px" },
];

const SIZE_FIELDS = [
  { key: "w", label: "Width", type: "select-labeled", default: "", options: WIDTH_OPTIONS },
  { key: "h", label: "Height", type: "select-labeled", default: "", options: HEIGHT_OPTIONS },
];

export const UTIL_WIDGETS = [
  {
    id: "weather",
    Icon: Cloud,
    title: "Free live weather widget",
    subtitle: "Open-Meteo · free · no API key",
    desc: "Live weather for any 1-3 cities. Six themes. No API key needed.",
    embed: "weather.js",
    needsSite: false,
    options: [
      {
        key: "cities",
        label: "Cities (search & pick up to 3)",
        type: "multi-search",
        default: "New York",
        max: 3,
        options: CITY_OPTIONS,
        placeholder: "Type a city name…",
      },
      { key: "units", label: "Units", type: "select", default: "metric", options: ["metric", "imperial"] },
      { key: "theme", label: "Theme", type: "select", default: "obsidian", options: ["obsidian", "indigo", "ivory", "sunset", "ocean", "transparent"] },
      ...SIZE_FIELDS,
    ],
  },
  {
    id: "crypto-ticker",
    Icon: Bitcoin,
    title: "Free crypto ticker widget",
    subtitle: "CoinGecko · cached · refreshes every few seconds",
    desc: "Live BTC/ETH/SOL/etc prices with 24h change. Pick from the top 80 coins.",
    embed: "crypto-ticker.js",
    needsSite: false,
    options: [
      {
        key: "ids",
        label: "Coins (search & pick up to 3)",
        type: "multi-search",
        default: "bitcoin,ethereum,solana",
        max: 3,
        options: CRYPTO_OPTIONS,
        placeholder: "Type a coin name or symbol…",
      },
      { key: "vs", label: "Quote currency", type: "select", default: "usd", options: ["usd", "eur", "gbp", "jpy", "inr"] },
      { key: "layout", label: "Layout", type: "select", default: "scroll", options: ["scroll", "stack"] },
      { key: "theme", label: "Theme", type: "select", default: "obsidian", options: ["obsidian", "ivory", "indigo", "neon", "sunset", "ocean", "transparent"] },
      ...SIZE_FIELDS,
    ],
  },
  {
    id: "stock-ticker",
    Icon: TrendingUp,
    title: "Free stock ticker widget",
    subtitle: "Live quotes · no API key · unlimited",
    desc: "Live stock prices with change %. Pick from the top 75 most-traded symbols. US and international exchanges.",
    embed: "stock-ticker.js",
    needsSite: false,
    options: [
      {
        key: "symbols",
        label: "Stocks (search & pick up to 3)",
        type: "multi-search",
        default: "AAPL,MSFT,GOOGL",
        max: 3,
        options: STOCK_OPTIONS,
        placeholder: "Type a ticker symbol or company…",
      },
      { key: "layout", label: "Layout", type: "select", default: "scroll", options: ["scroll", "stack"] },
      { key: "theme", label: "Theme", type: "select", default: "obsidian", options: ["obsidian", "ivory", "indigo", "sunset", "ocean", "transparent"] },
      ...SIZE_FIELDS,
    ],
  },
  {
    id: "countdown",
    Icon: Calendar,
    title: "Free countdown widget",
    subtitle: "Seasonal · presets included",
    desc: "Black Friday, Christmas, your custom launch date. Auto-renews each year for presets.",
    embed: "countdown.js",
    needsSite: false,
    options: [
      { key: "target", label: "Preset or ISO date", type: "select-labeled", default: "black-friday", options: [
        { value: "black-friday", label: "Black Friday" },
        { value: "cyber-monday", label: "Cyber Monday" },
        { value: "christmas", label: "Christmas" },
        { value: "new-year", label: "New Year" },
        { value: "valentines", label: "Valentine's Day" },
        { value: "halloween", label: "Halloween" },
        { value: "easter", label: "Easter (approx.)" },
        { value: "custom", label: "Custom launch date…" },
      ] },
      { key: "custom_date", label: "Custom launch date & time (only used when 'Custom' is picked)", type: "datetime", default: "", showWhen: { key: "target", equals: "custom" }, emptyHint: "Pick a date & time above — until you do, the countdown defaults to 24 hours from now." },
      { key: "label", label: "Custom label (optional)", type: "text", default: "" },
      { key: "zero_text", label: "Text to show when timer hits 0 (optional)", type: "text", default: "", placeholder: "e.g. Sale is here! · Doors are open · See you next year!" },
      { key: "sz", label: "Size", type: "select-labeled", default: "card", options: SIZE_OPTIONS_HCP },
      { key: "theme", label: "Theme", type: "select", default: "obsidian", options: ["obsidian", "ivory", "indigo", "sunset", "ocean", "candy", "noir", "transparent"] },
      { key: "show_seconds", label: "Show seconds", type: "boolean", default: "1" },
      ...SIZE_FIELDS,
    ],
  },
  {
    id: "visitor-globe",
    Icon: Globe,
    title: "Free visitor map widget",
    subtitle: "Rotating globe · or flat map · sky-blue + 6 more themes",
    desc: "Glowing dots where your visitors are, refreshed every 15-30s. Hover any dot for the country and its visit count. Enter your website URL — we create a Site ID for you and the globe starts pinning live visitors after install.",
    embed: "visitor-globe.js",
    needsSite: true,
    needsSiteInline: true,
    options: [
      { key: "map", label: "Style", type: "select", default: "globe", options: ["globe", "flat"] },
      { key: "sz", label: "Size", type: "select-labeled", default: "md", options: SIZE_OPTIONS },
      { key: "theme", label: "Theme", type: "select", default: "sky", options: ["sky", "obsidian", "indigo", "ivory", "neon", "sunset", "transparent"] },
      { key: "speed", label: "Rotation speed", type: "select-labeled", default: "normal", options: [
        { value: "off", label: "Off (static)" },
        { value: "slow", label: "Slow" },
        { value: "normal", label: "Normal" },
        { value: "fast", label: "Fast" },
        { value: "turbo", label: "Turbo" },
      ] },
      ...SIZE_FIELDS,
    ],
  },
  {
    id: "threads",
    Icon: MessageSquare,
    title: "Free comments widget (Disqus alternative)",
    subtitle: "Disqus killer · 5 KB · anonymous · rich custom theming",
    desc: "Free anonymous comments with everything Disqus paywalled: real-time push, threaded replies, reactions, custom accent colors, dark/light/auto, and zero tracking.",
    embed: "threads.js",
    needsSite: false,
    options: [
      { key: "theme", label: "Theme", type: "select", default: "transparent", options: ["transparent", "ivory", "indigo", "sunset", "ocean", "rose", "mint", "obsidian"] },
      { key: "accent", label: "Accent color", type: "select", default: "klein", options: ["klein", "emerald", "amber", "rose", "violet", "cyan", "noir"] },
      // v60.44 — `auto` removed: when a site embeds the widget, the
      // operator typically wants the widget to MATCH their site's
      // canonical theme regardless of the visitor's OS preference.
      // The old `auto` default forced dark-mode visitors to see a
      // dark widget on a light site, which looked broken. Sites that
      // truly want OS-following can still hand-edit the embed snippet.
      { key: "mode", label: "Color mode", type: "select", default: "light", options: ["light", "dark"] },
      { key: "reactions", label: "Show reactions (👍 ❤️ 🔥)", type: "boolean", default: "1" },
      { key: "realtime", label: "Real-time new comments", type: "boolean", default: "1" },
      { key: "threading", label: "Threaded replies", type: "boolean", default: "1" },
      { key: "markdown", label: "Markdown support", type: "boolean", default: "1" },
      // v60.44 — Optional moderation email. When set, every new
      // comment is held in a pending state and the operator gets a
      // one-click Approve / Reject email. Leave blank to auto-publish
      // every comment (the original Disqus-killer default).
      { key: "mod_email", label: "Moderation email (optional)", type: "text", default: "", placeholder: "you@example.com",
        hint: "Enter your email to moderate every new comment. We'll email you a one-click Approve / Reject link, and the comment stays hidden until you act. Leave blank to auto-publish everything." },
      { key: "ph", label: "Placeholder", type: "text", default: "Share your thoughts… (anonymous, no signup)" },
      ...SIZE_FIELDS,
    ],
  },
  {
    id: "verified-traffic",
    Icon: ShieldCheck,
    title: "Verified traffic trust badge",
    subtitle: "Media-kit trust badge",
    desc: "'Verified · 84K real visitors/mo' for advertisers. Enter your website URL — we create a Site ID for you instantly.",
    embed: "verified-traffic.js",
    needsSite: true,
    needsSiteInline: true,
    options: [
      { key: "sz", label: "Size", type: "select-labeled", default: "card", options: SIZE_OPTIONS_HCP },
      { key: "theme", label: "Theme", type: "select", default: "ivory", options: ["ivory", "obsidian", "indigo", "sunset", "transparent"] },
      ...SIZE_FIELDS,
    ],
  },
  {
    id: "youtube-live",
    Icon: Tv,
    title: "Free YouTube live widget",
    subtitle: "Auto-detects live broadcasts",
    desc: "🔴 LIVE NOW indicator that auto-hides offline. Default channel UCSJ4gkVC6NrvII8umztf0Ow (Lofi Girl — confirmed 24/7 live since 2017) is pre-filled so the live preview always populates without any setup.",
    embed: "youtube-live.js",
    needsSite: false,
    options: [
      { key: "channel", label: "Channel ID (UC...)", type: "text", default: "UCSJ4gkVC6NrvII8umztf0Ow", placeholder: "UCSJ4gkVC6NrvII8umztf0Ow (Lofi Girl — 24/7 live demo)" },
      { key: "theme", label: "Theme", type: "select", default: "red", options: ["red", "obsidian", "ivory", "indigo", "transparent"] },
      { key: "hide_offline", label: "Hide when offline", type: "boolean", default: "1" },
    ],
  },
  {
    id: "twitch-live",
    Icon: TwitchIcon,
    title: "Free Twitch live widget",
    subtitle: "Live viewer count + game",
    desc: "🟣 LIVE · viewer count + current game, and it hides itself when the channel goes offline. Pre-filled with 'lofigirl' (a round-the-clock music stream) so the preview populates without any setup.",
    embed: "twitch-live.js",
    needsSite: false,
    options: [
      // v60.102.17 — monstercat stopped running its 24/7 Twitch simulcast, so
      // the documented "always populates" default rendered the offline state
      // (verified against the Twitch API: offline). Switched to lofigirl,
      // which is genuinely live round the clock and matches the YouTube
      // widget's default channel. The preview panes also pass demo=1, so a
      // channel going dark never makes the gallery look broken.
      { key: "username", label: "Twitch username", type: "text", default: "lofigirl", placeholder: "lofigirl — 24/7 demo · or your own: xqc, shroud, kaicenat…" },
      { key: "theme", label: "Theme", type: "select", default: "purple", options: ["purple", "obsidian", "ivory", "indigo", "transparent"] },
      { key: "hide_offline", label: "Hide when offline", type: "boolean", default: "1" },
    ],
  },
  {
    id: "ask-site",
    Icon: Sparkles,
    title: "Free AI chat widget for your site",
    subtitle: "Powered by GPT-5.2 · 7 colorful themes",
    desc: "Floating AI bubble. Answers from indexed page content. When AI can't answer, visitor can submit name + email + question — forwarded straight to your inbox. Preview shows the bubble inside a sandboxed demo site, not on this page.",
    embed: "ask-site.js",
    needsSite: true,
    needsSiteInline: true,
    sandboxedPreview: true,
    options: [
      { key: "pos", label: "Position", type: "select-labeled", default: "br", options: [
        { value: "br", label: "Bottom right" },
        { value: "bl", label: "Bottom left" },
        { value: "tr", label: "Top right" },
        { value: "tl", label: "Top left" },
      ] },
      { key: "size", label: "Bubble size", type: "select-labeled", default: "md", options: SIZE_OPTIONS },
      { key: "anim", label: "Animation", type: "select-labeled", default: "pulse", options: [
        { value: "none", label: "None (static)" },
        { value: "pulse", label: "Pulse (gentle scale)" },
        { value: "bounce", label: "Bounce" },
        { value: "sparkle", label: "Sparkle glow" },
        { value: "glow", label: "Ripple ring" },
      ] },
      { key: "theme", label: "Theme", type: "select", default: "indigo", options: ["indigo", "obsidian", "ivory", "sunset", "ocean", "rose", "emerald", "neon"] },
      { key: "greeting", label: "Greeting", type: "text", default: "Hi! Ask me anything about this page." },
      { key: "button", label: "Button label", type: "text", default: "Ask AI" },
    ],
  },
  {
    id: "page-speed-badge",
    Icon: Gauge,
    title: "Free page speed badge",
    subtitle: "PageSpeed Insights · 'This site loads in X ms'",
    desc: "Self-referential brag widget. Re-checks every 6 hours once the site is registered. Preview shows a placeholder (marked with a pulsing dot) until the real PageSpeed result arrives.",
    embed: "badge/speed.js",
    embedIsBadge: true,
    needsSite: false,
    options: [
      // v60.61 — single URL field. Accepts a root domain (`feed-pulse.com`)
      // OR a full URL with path (`https://feed-pulse.com/blog/post-1`). The
      // preview measures whatever URL is entered (homepage or internal
      // page). The COPIED snippet only carries the domain so the badge
      // auto-detects `window.location.href` on the customer's site —
      // paste it in `<head>` once → every page reports ITS OWN score.
      { key: "d", label: "URL to measure", type: "text", default: "feed-pulse.com", urlHint: true, placeholder: "https://feed-pulse.com/blog/post-1 — or just feed-pulse.com", hint: "Paste any page URL (homepage or internal). The preview shows that page's score. The snippet you copy auto-detects each page it's installed on — paste it once in <head> to get per-page scores site-wide." },
      { key: "sz", label: "Size", type: "select-labeled", default: "card", options: [...SIZE_OPTIONS_HCP, { value: "micro", label: "Micro" }] },
      { key: "theme", label: "Theme", type: "select", default: "obsidian", options: ["indigo", "obsidian", "ivory", "neon", "mint", "sunset", "ocean", "noir", "candy", "transparent"] },
      ...SIZE_FIELDS,
    ],
  },
  {
    id: "online-visitors",
    Icon: Users,
    title: "Live online visitors counter",
    subtitle: "Real-time online users · 6 themes · server-side bot filter",
    desc: "How many people are reading your site right now? Live counter that updates the moment a visitor lands and ticks down 5 minutes after they leave. Server-side bot filter. Free forever, no signup.",
    embed: "online-visitors.js",
    needsSite: true,
    needsSiteInline: true,
    options: [
      { key: "theme", label: "Theme", type: "select", default: "obsidian", options: ["obsidian", "ivory", "neon", "mint", "indigo", "ocean"] },
      { key: "sz", label: "Size", type: "select-labeled", default: "card", options: [...SIZE_OPTIONS_HCP, { value: "micro", label: "Micro" }] },
    ],
  },
  {
    // Cumulative all-time visit counter — the 1996 GeoCities-classic
    // remake. Distinct from `online-visitors` which counts only users
    // active in a 5-min live window. This one counts every page load
    // since installation and never decreases.
    id: "hit-counter",
    Icon: Activity,
    title: "Free Hit Counter",
    subtitle: "Cumulative all-time visits · LED-glow · base + reset knobs",
    desc: "Every page load increments your total. Bots filtered server-side. Customize the starting base (e.g. seed at 1,000) and an optional auto-reset trigger (e.g. wrap back to 0 after hitting 1,000,000). The free GoatCounter / StatCounter alternative.",
    embed: "hit-counter.js",
    needsSite: true,
    needsSiteInline: true,
    options: [
      { key: "theme", label: "Theme", type: "select", default: "obsidian", options: ["obsidian", "ivory", "neon", "mint", "indigo", "ocean"] },
      { key: "style", label: "Style", type: "select", default: "led", options: ["led", "flat", "card", "pill"] },
      { key: "base", label: "Starting base (0 = start fresh)", type: "number", default: "0", placeholder: "0" },
      { key: "reset_at", label: "Reset trigger (0 = never)", type: "number", default: "0", placeholder: "1000000" },
      { key: "reset_to", label: "Reset to value", type: "number", default: "0", placeholder: "0" },
      { key: "digits", label: "Display digits (zero-padded width)", type: "number", default: "7", placeholder: "7" },
    ],
  },
  {
    // v60.99.99 — the flag counter finally gets a registry entry so it can
    // have its own SEO landing (/free-flag-counter-widget). Previously it
    // existed only as a Generator tab, which is robots-Disallowed, so the
    // highest-volume keyword in the catalogue had no indexable home and the
    // blog's internal links to /free-flag-counter-widget were orphans.
    // Colour params are sent WITHOUT the leading '#' — flag_counter.js
    // prepends it (see `var BG="#"+(CFG.bc||"ffffff")`).
    id: "flag-counter",
    Icon: FlagIcon,
    title: "Free flag counter",
    subtitle: "Ranked country list · live · Lighthouse-safe",
    desc: "Count every country that visits and show them as a ranked flag list. Flags lazy-load below the fold so your performance score doesn't move. Bots filtered server-side. Free forever, no signup.",
    embed: "flag-counter.js",
    needsSite: true,
    needsSiteInline: true,
    options: [
      { key: "rows", label: "Countries shown", type: "number", default: "10", placeholder: "10" },
      { key: "cols", label: "Columns", type: "number", default: "3", placeholder: "3" },
      { key: "cod", label: "Show country code", type: "boolean", default: "1" },
      { key: "w", label: "Width", type: "select-labeled", default: "320", options: WIDTH_OPTIONS },
      { key: "bc", label: "Background", type: "select-labeled", default: "ffffff", options: [
        { value: "ffffff", label: "White" },
        { value: "0a0a0a", label: "Obsidian" },
        { value: "0f172a", label: "Midnight" },
        { value: "faf7f0", label: "Paper" },
      ] },
      { key: "hb", label: "Header colour", type: "select-labeled", default: "002fa7", options: [
        { value: "002fa7", label: "Klein blue" },
        { value: "0a0a0a", label: "Noir" },
        { value: "047857", label: "Emerald" },
        { value: "b45309", label: "Amber" },
      ] },
    ],
  },
  {
    id: "clicks-heatmap",
    Icon: Flame,
    title: "Clicks Heatmap",
    subtitle: "See exactly where visitors click on any page",
    desc: "Install once → return to feed-pulse.com to see a click heatmap layered on every page of your site. Spot dead zones, hot CTAs, and ignored nav items in 30 seconds. Per-page breakdowns, top-clicked elements, blazing/hot/warm/smoky temperature bins.",
    embed: "clicks-heatmap.js",
    needsSite: true,
    needsSiteInline: true,
    // Silent click-event tracker — no visible UI when running on a real
    // site, so the WidgetLanding preview box swaps the embed-script demo
    // for a link-through to /sample-heatmap (fully-interactive demo with
    // sample data, real visualisation). Same growth funnel, better UX
    // than a dead "needs site_id" cold-start message.
    previewLinkTo: "/sample-heatmap",
    previewLinkLabel: "See the heatmap live →",
    previewLinkSubtext: "Open the interactive sample heatmap — real visualisation, sample data, zero install.",
    installHint: "Paste the snippet inside the <head> tag of every page you want to track (or just before </body> for slightly later page loads). Most CMSes have a 'Header scripts' / 'Custom HTML' setting for this — WordPress: Appearance → Theme File Editor → header.php · Shopify: Theme → Edit code → theme.liquid · Webflow: Project settings → Custom code → Head.",
    options: [
      { key: "scope", label: "Track which pages?", type: "select-labeled", default: "all", options: [
        { value: "all", label: "All pages on this domain" },
        { value: "page", label: "Only one specific URL (path below)" },
      ] },
      { key: "path", label: "Path to track (only if 'one specific URL')", type: "text", default: "/", placeholder: "/pricing" },
      { key: "sample", label: "Click capture rate", type: "select-labeled", default: "1.0", options: [
        { value: "1.0", label: "Capture every click (recommended)" },
        { value: "0.5", label: "Capture 50% of clicks (lighter load)" },
        { value: "0.25", label: "Capture 25% of clicks" },
        { value: "0.1", label: "Capture 10% of clicks (high-traffic sites)" },
      ] },
    ],
  },
  {
    // v60.87 — first entry in the Widget Galaxy weekly expansion.
    // Site-agnostic (no site_id), reads CFG.repo as "owner/repo".
    // Backed by `/api/github/stars/{owner}/{repo}` proxy (6h per-repo
    // cache → safely shares one upstream call across thousands of
    // embeds within GH's 60 req/hr unauthenticated tier).
    id: "github-stars",
    Icon: Star,
    title: "Free GitHub stars badge",
    subtitle: "★ 42.3k stars · auto-updating pill",
    desc: "Show off your GitHub repo's star count anywhere — README, landing page, docs, blog. 6 themes (classic, obsidian, ivory, indigo, emerald, transparent), auto-refreshes hourly, single script tag.",
    embed: "github-stars.js",
    needsSite: false,
    options: [
      { key: "repo", label: "Repo (owner/name)", type: "text", default: "facebook/react", placeholder: "facebook/react — torvalds/linux — vercel/next.js" },
      { key: "theme", label: "Theme", type: "select", default: "classic", options: ["classic", "obsidian", "ivory", "indigo", "emerald", "transparent"] },
    ],
  },
  {
    // v60.88 — second Widget Galaxy entry. Same developer audience as
    // GitHub stars badge — pairs naturally on dev portfolios, OSS landing
    // pages, and npm package docs. Backed by api.npmjs.org/downloads
    // (free, no auth) with a 6h per-(period, pkg) server-side cache.
    id: "npm-downloads",
    Icon: Package,
    title: "Free npm downloads badge",
    subtitle: "npm 3.2M / week · auto-updating pill",
    desc: "Show off your npm package's download count anywhere — landing page, blog, docs, portfolio. Last-day / last-week / last-month period, 7 themes (npm, obsidian, ivory, indigo, emerald, slate, transparent), single script tag.",
    embed: "npm-downloads.js",
    needsSite: false,
    options: [
      { key: "package", label: "Package name", type: "text", default: "react", placeholder: "react — @types/node — lodash — next" },
      { key: "period", label: "Period", type: "select-labeled", default: "last-week", options: [
        { value: "last-day", label: "Last day" },
        { value: "last-week", label: "Last week" },
        { value: "last-month", label: "Last month" },
      ] },
      { key: "theme", label: "Theme", type: "select", default: "npm", options: ["npm", "obsidian", "ivory", "indigo", "emerald", "slate", "transparent"] },
    ],
  },
  {
    // v60.97 — third Widget Galaxy entry. Community / server-site
    // audience (Discord communities, OSS project landing pages,
    // creator portfolios). Backed by Discord's public widget.json
    // endpoint (free, no auth) with a 6h per-guild cache. Requires the
    // server's "Widget" feature to be enabled (Server Settings → Widget).
    id: "discord-members",
    Icon: Hash,
    title: "Free Discord server widget",
    subtitle: "● 3,421 online · auto-updating widget",
    desc: "Show your Discord community's live online-members count anywhere — landing page, OSS docs, creator portfolio, blog. 7 themes (Discord blurple, obsidian, ivory, indigo, emerald, slate, transparent), auto-refreshes hourly, single script tag. Click-through opens your server's instant invite (if set).",
    embed: "discord-members.js",
    needsSite: false,
    options: [
      { key: "guild", label: "Discord server ID", type: "text", default: "267624335836053506", placeholder: "267624335836053506 — Server Settings → Widget → Server ID" },
      { key: "theme", label: "Theme", type: "select", default: "discord", options: ["discord", "obsidian", "ivory", "indigo", "emerald", "slate", "transparent"] },
      { key: "show_name", label: "Show server name", type: "boolean", default: "0" },
    ],
  },
];

export function findUtilWidget(id) {
  return UTIL_WIDGETS.find((w) => w.id === id);
}

/* Stable widget IDs referenced from other modules. Using named constants
 * (instead of inline string literals) means a rename in UTIL_WIDGETS gets
 * caught at import time anywhere they're consumed — e.g. by the homepage
 * FeaturedRotator and the social-proof RecentInstallsStrip. */
export const WIDGET_ID = Object.freeze({
  WEATHER: "weather",
  CRYPTO_TICKER: "crypto-ticker",
  STOCK_TICKER: "stock-ticker",
  COUNTDOWN: "countdown",
  VISITOR_GLOBE: "visitor-globe",
  THREADS: "threads",
  VERIFIED_TRAFFIC: "verified-traffic",
  YOUTUBE_LIVE: "youtube-live",
  TWITCH_LIVE: "twitch-live",
  ASK_SITE: "ask-site",
  PAGE_SPEED_BADGE: "page-speed-badge",
  CLICKS_HEATMAP: "clicks-heatmap",
});

/* The 4 widgets featured on the /widgets spotlight rotator, in cycle order.
 * Validated against UTIL_WIDGETS on module load so a rename trips an
 * obvious error instead of silently null-ing the rotator. */
export const FEATURED_WIDGET_IDS = Object.freeze([
  WIDGET_ID.COUNTDOWN,
  WIDGET_ID.VISITOR_GLOBE,
  WIDGET_ID.THREADS,
  WIDGET_ID.CLICKS_HEATMAP,
]);

// v60.99.10 — shared size preset for the 5 pill-style badge widgets
// (YouTube Live, Twitch Live, GitHub Stars, NPM Downloads, Discord
// Members). All 5 share the same pill geometry, so size scaling is
// uniform: padding + font-size + dot-size are interpolated together
// to keep the visual identity consistent across the 3 presets.
//
// Why 3 sizes and not 4? "Small" maps to inline-with-text use cases
// (footers, sidebars, blog post inline mentions). "Regular" is the
// card-footer / dev-portfolio default. "Large" is the hero CTA /
// project landing tile. A 4th step would force most users to think
// about a marginal difference (regular vs medium-large) that adds
// no real adoption value.
const BADGE_SZ_FIELD = {
  key: "sz",
  label: "Badge size",
  type: "select-labeled",
  default: "regular",
  options: [
    { value: "small", label: "Small — for inline use in footers / sidebars" },
    { value: "regular", label: "Regular — card-footer default" },
    { value: "large", label: "Large — hero CTA / landing tile" },
  ],
};


// `.js` ships an i18n dict (see /app/backend/widgets/*). When the user
// picks a language in the customizer, the value flows through as
// `?lang=es` (etc.) to the embed snippet — the widget JS reads CFG.lang
// and renders all UI strings in that language. Clients can also override
// any string at the widget level via existing CFG fields (CFG.label,
// CFG.button, CFG.greeting, CFG.ph) so brand-specific copy still wins.
//
// Languages list matches SITE_LANGS in /lib/i18n.js — 12 locales total.
// Default is "auto" which means: send no `lang=` param, the widget JS
// falls back to its English bundle. We use "auto" rather than "en" so
// a future enhancement (auto-detect the host page's <html lang> attr in
// the embed snippet) is non-breaking.
const WIDGET_LANG_OPTIONS = [
  { value: "auto", label: "Auto (English)" },
  { value: "es", label: "Español" },
  { value: "fr", label: "Français" },
  { value: "de", label: "Deutsch" },
  { value: "hi", label: "हिन्दी" },
  { value: "pt", label: "Português" },
  { value: "it", label: "Italiano" },
  { value: "ru", label: "Русский" },
  { value: "ja", label: "日本語" },
  { value: "zh", label: "中文" },
  { value: "ar", label: "العربية" },
  { value: "nl", label: "Nederlands" },
];
const LANG_FIELD = {
  key: "lang",
  label: "Widget language",
  type: "select-labeled",
  default: "auto",
  options: WIDGET_LANG_OPTIONS,
};
// Widgets whose JS does NOT ship an i18n dict (visitor-globe has only
// proper-noun region labels; clicks-heatmap is invisible/admin-only).
// Skip them so the customizer UI stays clean.
const _NO_I18N_WIDGETS = new Set(["visitor-globe", "clicks-heatmap"]);
for (const w of UTIL_WIDGETS) {
  if (_NO_I18N_WIDGETS.has(w.id)) continue;
  if (w.options.some((o) => o.key === "lang")) continue; // idempotent
  w.options = [...w.options, LANG_FIELD];
}

// v60.99.10 — auto-inject the shared Badge size selector into the 5
// pill-style badge widgets. Each accepts the same `sz` preset so the
// embed snippet has a uniform URL shape (`?sz=large` etc.) across the
// family. Same idempotency pattern as the language injector above.
const _SZ_BADGE_WIDGETS = new Set([
  "youtube-live",
  "twitch-live",
  "github-stars",
  "npm-downloads",
  "discord-members",
]);
for (const w of UTIL_WIDGETS) {
  if (!_SZ_BADGE_WIDGETS.has(w.id)) continue;
  if (w.options.some((o) => o.key === "sz")) continue; // idempotent
  w.options = [...w.options, BADGE_SZ_FIELD];
}

// Dev-time guard — runs once at import. Throws in non-production so a
// rename mistake surfaces loudly on hot reload / CI instead of silently
// rendering an empty FeaturedRotator. Stripped in production builds.
if (process.env.NODE_ENV !== "production") {
  for (const id of FEATURED_WIDGET_IDS) {
    if (!UTIL_WIDGETS.some((w) => w.id === id)) {
      throw new Error(
        `[util_widgets] FEATURED_WIDGET_IDS references unknown widget id "${id}". ` +
        `Update either UTIL_WIDGETS or FEATURED_WIDGET_IDS so they match.`
      );
    }
  }
}
